import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { useToasts } from 'react-toast-notifications';
import { ValueType, OptionsType } from 'react-select';
import moment from 'moment';

import Loader from '../loader';
import Select from './select';

import { RootState } from '../../../reducers/app';
import { toggleMapLoader } from '../../../reducers/mapview/actions';

import { IImagePair, IImageryLayer } from '../../../types/timeline';
import { IOptionsType } from '../../../types/react-select-options';

import config from '../../../config/config';

import MapController from '../../../controllers/MapController';
import TimelineController from '../../../controllers/TimelineController';

import Icon from '../../../assets';

import './analyze-imagery.scss';
import LayerTooltip from './layer-components/layer-tooltip';
import InfoSvg from '../../../assets/info-icon';

/**
 * Component enabling a user to analyze two images
 */
const AnalyzeImagery = () => {
  const dispatch = useDispatch();
  // LOCAL STATE
  const [readyForAnalysis, setReadyForAnalysis] = useState<boolean>(false);
  const [vegetationIndex, setVegetationIndex] = useState<IOptionsType>({ value: 'NDVI', label: 'NDVI' });

  // REDUX
  const imagePairs = useSelector((state: RootState) => state.auditdata.selectedImagePairs.imagePairs);
  const currentImagePair = useSelector((state: RootState) => state.auditdata.currentImagePair);
  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const timelineOpen = useSelector((state: RootState) => state.auditdata.timelineOpen);
  const auditArea = useSelector((state: RootState) => state.auditdata.auditArea.graphic);

  useEffect(() => {
    setReadyForAnalysis(imagePairs.length > 0 && !!isOptionsType(vegetationIndex));
  }, [imagePairs, vegetationIndex]);

  const { addToast } = useToasts();

  /**
   *
   * @param isBefore - a boolean describing whether the Before or After image is being selected
   */
  const handleToggleTimeline = () => {
    dispatch({ type: 'SET_TIMELINE_OPEN', open: true });
    dispatch({ type: 'SET_TIME_BEING_SELECTED', isBefore: true });
  };

  /**
   * Handles the deletion of the before/after image
   * @param pair - the pair to delete
   */
  const handleDeletePair = (pair: IImagePair) => {
    // If we have the Swipe widget toggled, we want to make sure to destroy it when we delete the last image pair
    if (MapController._swipe && imagePairs.length === 1) {
      MapController.toggleSwipeWidget(true);
    }
    dispatch({ type: 'REMOVE_IMAGE_PAIR', imagePair: pair });
    // We want to remove the before highlight from the bounding box layer
    MapController.toggleBoundingBoxHighlight(
      pair.before.acquisitionDate,
      pair.before.highlightID,
      IImageryLayer[pair.before.imageLayer],
      false
    );
    TimelineController.addSelectedImagesToMap();
  };

  /**
   * Displays DOM component containing information about the selected image. This is only displayed when an iamge has
   * been selected as the before/after image.
   */
  const renderSelectedImages = () => {
    return imagePairs.map((pair) => (
      <div className={`selected-image-pair`}>
        {Object.keys(pair).map((key, idx) => (
          <div className='selected-image' key={idx}>
            <p className='selected-image-date'>{moment(pair[key].acquisitionDate).format('MMM Do YYYY')}</p>
            <p className='selected-image-time'>{moment(pair[key].acquisitionDate).format('h:mm:ss')}</p>
            <div className='selected-image-cloudcover'>
              <Icon name='cloud' height='14' width='14' viewBox='0 0 24 24' color='#4a4a4a' />
              <p>{`${pair[key].cloudcover > 0 ? '<' : ''} ${pair[key].cloudcover}%`}</p>
            </div>
          </div>
        ))}
        <div onClick={() => handleDeletePair(pair)} className='selected-image__info-button'>
          <Icon name='close' height='16' width='16' viewBox='0 0 18 18' />
        </div>
      </div>
    ));
  };

  const runRasterAnalysis = () => {
    MapController.createGroupRaster(vegetationIndex!.value)
      .then((res: string) => {
        addToast(res, { appearance: 'success', autoDismiss: true });
        setReadyForAnalysis(false);
        // setTimeouts are bad, but we also have no way of ejecting out/unlocking the app if a pixelFilter fails to create on the layer
        // TODO: Refactor the loader logic to have unique loaders for each of these layers (pass function into create group raster)
        // this will allow for more specificity and potentially let us remove this setTimeout
        setTimeout(() => {
          dispatch(toggleMapLoader(false));
        }, 60000);
      })
      .catch((res: string) => {
        addToast(res, { appearance: 'error', autoDismiss: true });
        dispatch(toggleMapLoader(false));
        setReadyForAnalysis(false);
      });
  };

  const createRasterOptions = () => {
    return config.leftpanel.vegetationIndexes.map((option: string) => {
      return { value: option, label: option };
    });
  };

  const isOptionsType = (layers: ValueType<IOptionsType>): layers is OptionsType<IOptionsType> => {
    return !!layers;
  };

  const disableImageSelectionButton = () => {
    if (timelineOpen) {
      return true;
    }
    if (!auditArea) {
      return true;
    }
    return false;
  };

  const renderSelectImagesButtonText = () => {
    let text = 'Select Images';
    if (imagePairs.length > 0) {
      text = 'Select Additional Image Pair';
    }
    if (currentImagePair.before && currentImagePair.before.locked) {
      text = `Before Image: ${moment(currentImagePair.before.acquisitionDate).format(
        'MMM Do YYYY'
      )}. Select After Image`;
    }
    return text;
  };

  return (
    <div className='analyze-imagery'>
      {loaderActive && <Loader />}
      <div className='analyze-imagery__selector'>
        <p className='analyze-imagery__selector-title'>Audit Period</p>
        <div className='analyze-imagery__selector-images'>
          <div className='image-data'>
            {imagePairs && imagePairs.length > 0 && renderSelectedImages()}
            <button
              disabled={disableImageSelectionButton()}
              className={`image-data__button ${timelineOpen ? 'disabled' : ''}`}
              onClick={() => handleToggleTimeline()}
            >
              <p>{renderSelectImagesButtonText()}</p>
              <Icon name='image' />
            </button>
            <div className='analyze-imagery__info'>
              <p>About choosing images</p>
              <LayerTooltip id='tooltip-choose' tip={config.helpertext.imageAnalysisSelection}>
                <InfoSvg />
              </LayerTooltip>
            </div>
            <ReactTooltip
              className='tooltip choose-image'
              id='tooltip-choose'
              place='right'
              multiline={true}
              type='success'
              effect='solid'
              clickable={true}
            />
          </div>
        </div>
        <div className='analyze-imagery__selector-vegetation'>
          <p className='vegetation-description'>Select a Vegetation index to use when comparing your images</p>
          <div className='vegetation-select-wrapper'>
            <Select
              isMulti={false}
              options={createRasterOptions()}
              value={vegetationIndex}
              onChange={setVegetationIndex}
              isDisabled={false}
              isClearable={false}
              placeholder='Select Vegetation Index'
            />
            <LayerTooltip id='tooltip-veg-index-info' tip={config.helpertext.vegetationIndexInfo}>
              <InfoSvg />
            </LayerTooltip>
            <ReactTooltip
              className='tooltip veg-index-info'
              id='tooltip-veg-index-info'
              place='right'
              multiline={true}
              type='success'
              effect='solid'
              clickable={true}
            />
          </div>
        </div>
        <div className='analyze-imagery__analyze'>
          <button
            className={`analyze-imagery__analyze-button ${readyForAnalysis ? '' : 'disabled'}`}
            onClick={() => runRasterAnalysis()}
            disabled={!readyForAnalysis}
          >
            Run Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyzeImagery;
